import UpgradeTooltip from '@anm/components/UpgradeTooltip';
import IconButton from '@anm/components/buttons/IconButton';
import Dialog from '@anm/components/modals/Dialog';
import ConfirmDelete from '@anm/components/notification/ConfirmDelete';
import useDependPendingCallback from '@anm/hooks/useDependPendingCallback';
import { useState, FC } from 'react';
import { UserTeamPermission } from 'types/user';

import OwnerAlert from '../TeamMemberRolesInput/OwnerAlert';

import InvitationItem, { InvitationItemInputProps } from './InvitationItem';
import TeamRolesModal, { TeamRolesModalProps } from './TeamRolesModal';
import Wrapper, { Grid, Header, Limit } from './Wrapper';

export type InvitationsListProps = Pick<TeamRolesModalProps, 'onRoleChange'> & {
  limit: number;
  isChangeRolePending: boolean;
  isDeletePending: boolean;
  canUpgrade: boolean;
  list: InvitationItemInputProps[];
  permissions?: UserTeamPermission;
  currentUserId?: string;
  onMemberDelete: (id: string) => void;
  onInviteDelete: (hash: string) => void;
  onInviteResend: (id: string) => void;
  onInviteMember: () => void;
};

const InvitationsList: FC<InvitationsListProps> = ({
  list,
  limit,
  canUpgrade,
  isChangeRolePending,
  isDeletePending,
  permissions,
  currentUserId,
  onRoleChange,
  onMemberDelete,
  onInviteResend,
  onInviteMember,
  onInviteDelete
}) => {
  const [userDeleteId, setUserDeleteId] = useState<string | null>(null);
  const [inviteDeleteId, setInviteDeleteId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [newOwnerId, setNewOwnerId] = useState<string | null>(null);

  const teamMembers = list.filter(({ isTeamMember }) => isTeamMember);
  const findMember = (id: string) => list.find(({ userId }) => userId === id);
  const makeOwner = () => {
    newOwnerId && onRoleChange({ role: 'OWNER', userId: newOwnerId });
    setNewOwnerId(null);
  };

  useDependPendingCallback(isChangeRolePending, () => setUserId(null));
  useDependPendingCallback(isDeletePending, () => {
    setUserDeleteId(null);
    setInviteDeleteId(null);
  });

  return (
    <Wrapper>
      {newOwnerId && (
        <OwnerAlert
          nextOwnerName={findMember(newOwnerId)?.userName || 'the member'}
          onClose={() => setNewOwnerId(null)}
          onMakeOwner={makeOwner}
        />
      )}

      {userDeleteId && (
        <Dialog onModalClose={() => setUserDeleteId(null)} onSubmit={() => onMemberDelete(userDeleteId)}>
          <ConfirmDelete
            title="Delete team member"
            description={`The Team member will be permanently<br/> removed from your team account, so<br/> they cannot see or create projects<br/> in the Team workspace.`}
            isPending={isDeletePending}
            onClose={() => setUserDeleteId(null)}
            onDelete={() => onMemberDelete(userDeleteId)}
          />
        </Dialog>
      )}
      {inviteDeleteId && (
        <Dialog onModalClose={() => setInviteDeleteId(null)} onSubmit={() => onInviteDelete(inviteDeleteId)}>
          <ConfirmDelete
            title="Delete invite"
            description={`The Team invite will be permanently removed.<br/> You can create new one`}
            isPending={isDeletePending}
            onClose={() => setInviteDeleteId(null)}
            onDelete={() => onInviteDelete(inviteDeleteId)}
          />
        </Dialog>
      )}
      {userId && (
        <TeamRolesModal
          currentUserId={currentUserId}
          userId={userId}
          permissions={permissions}
          isPending={isChangeRolePending}
          list={teamMembers}
          onRoleChange={onRoleChange}
          onModalClose={() => setUserId(null)}
        />
      )}
      <Header>
        <Limit isLink={canUpgrade}>
          <UpgradeTooltip
            minWidth={150}
            canShow={canUpgrade}
            buttonLink="/pricing"
            buttonText="Upgrade"
            text="Please upgrade to add more members."
          >
            {teamMembers.length}/{limit} seats
          </UpgradeTooltip>
        </Limit>
        {permissions?.canInvite && (
          <IconButton icon="images/dashboard/plus.svg" size="large_short" variant="light-blue" onClick={onInviteMember}>
            Invite Member
          </IconButton>
        )}
      </Header>
      <Grid>
        {list.map(props => (
          <InvitationItem
            key={`${props?.inviteKey}/${props?.userId}`}
            {...props}
            permissions={permissions}
            onChange={setUserId}
            onUserDelete={setUserDeleteId}
            onInviteDelete={setInviteDeleteId}
            onOwnerChange={setNewOwnerId}
            onInviteResend={onInviteResend}
          />
        ))}
      </Grid>
    </Wrapper>
  );
};

export default InvitationsList;
